import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './MinistriesMegaDropdown.css';
import { ministryService } from '../../services/ministry-service';
import { Language } from '../../constants/language';
import { Ministry } from '../../models/ministry/ministry';

interface MegaDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

const MinistriesMegaDropdown: React.FC<MegaDropdownProps> = ({ isOpen, onClose }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const [ministries, setMinistries] = React.useState<Ministry[]>([]);

  React.useEffect(() => {
    ministryService.getMinistries(i18n.language as Language).then(result => {
      setMinistries(result.data);
    });
  }, [i18n.language]);

  /****************
   * Events
   *****************/
  const handleMouseLeave = () => {  onClose(); };
  const handleMouseEnter = () => { };

  if (!isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      className="ministries-mega-dropdown"
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      style={{
        position: 'fixed',
        top: '80px',
        left: '0',
        right: '0',
        width: '100vw',
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 'var(--border-radius-xl)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: 'var(--shadow-xl)',
        zIndex: 1000,
        padding: 'var(--spacing-xl)',
        color: 'white'
      }}
    >
      <div className="ministries-mega-dropdown__container">
        {/* Header */}
        <div className="ministries-mega-dropdown__header" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
            <div style={{ 
              width: '3px', 
              height: '24px', 
              backgroundColor: 'white', 
              borderRadius: 'var(--border-radius-sm)' 
            }}></div>
            <h3 style={{ 
              fontSize: 'var(--font-size-xl)', 
              fontWeight: '700', 
              color: 'white', 
              margin: 0 
            }}>
              {t('nav.ministriesDropdown.title')}
            </h3>
          </div>
          <p style={{ 
            fontSize: 'var(--font-size-sm)', 
            color: 'rgba(255, 255, 255, 0.7)', 
            margin: 0 
          }}>
            {t('nav.ministriesDropdown.subtitle')}
          </p>
        </div>

        {/* Ministries Grid */}
        <div className="ministries-mega-dropdown__grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 'var(--spacing-md)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          {ministries.map((ministry) => (
            <Link
              key={ministry.id}
              to={`/ministries/${ministry.id}`}
              onClick={onClose}
              style={{
                display: 'block',
                padding: 'var(--spacing-md)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 'var(--border-radius-lg)',
                textDecoration: 'none',
                color: 'white',
                transition: 'all var(--transition-normal)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(4px)',
                  borderRadius: 'var(--border-radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  flexShrink: 0
                }}>
                  <img
                    src={ministry.media.logo.url}
                    alt={ministry.title}
                    style={{
                      width: '75px',
                      height: '75px',
                      objectFit: 'fill'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<div style="width: 32px; height: 32px; background: rgba(0,0,0,0.2); border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #000; font-size: 14px; font-weight: bold;">${ministry.title.charAt(0)}</div>`;
                      }
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    fontWeight: '600', 
                    margin: 0, 
                    lineHeight: '1.3',
                    color: 'white'
                  }}>
                    {ministry.title}
                  </h4>
                  <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'rgba(255, 255, 255, 0.6)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    padding: '2px 6px',
                    borderRadius: 'var(--border-radius-sm)',
                    display: 'inline-block',
                    marginTop: '2px'
                  }}>
                    {ministry.category || 'Faith'}
                  </span>
                </div>
              </div>
              <p style={{ 
                fontSize: 'var(--font-size-xs)', 
                color: 'rgba(255, 255, 255, 0.7)', 
                margin: 0,
                lineHeight: '1.4',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {ministry.description.length > 60 
                  ? `${ministry.description.substring(0, 60)}...` 
                  : ministry.description
                }
              </p>
            </Link>
          ))}
        </div>

        {/* Footer CTA */}
        <div style={{ 
          textAlign: 'center', 
          paddingTop: 'var(--spacing-md)', 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)' 
        }}>
          <Link
            to="/ministries"
            onClick={onClose}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)',
              backgroundColor: 'white',
              color: 'black',
              padding: 'var(--spacing-sm) var(--spacing-md)',
              borderRadius: 'var(--border-radius-lg)',
              textDecoration: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: '600',
              transition: 'all var(--transition-normal)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span>{t('nav.ministriesDropdown.viewAll')}</span>
            <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MinistriesMegaDropdown;